import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Plus, Upload, Search, Trash2, Edit, X, DollarSign, Download, FileSpreadsheet } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { PeriodFilter } from '../components/PeriodFilter';
import { Pagination } from '../components/shared/Pagination';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatNumber = (num) => {
  if (num === null || num === undefined) return '0.00';
  return Number(num).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const PAYMENT_METHODS = ['MoMo', 'Equity Bank', 'Bank of Kigali', 'Cash'];

const PurchasesPage = () => {
  const { hasPermission } = useAuth();
  const canDelete = hasPermission('delete_purchase');
  const [purchases, setPurchases] = useState([]);
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPurchase, setEditingPurchase] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [periodFilter, setPeriodFilter] = useState({ startDate: '', endDate: '', label: '' });
  const [customerFilter, setCustomerFilter] = useState('all');
  const [selectedIds, setSelectedIds] = useState([]);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [paymentPurchase, setPaymentPurchase] = useState(null);
  const [paymentForm, setPaymentForm] = useState({ amount: '', payment_method: 'Cash', payment_date: new Date().toISOString().split('T')[0] });
  const [purchasePayments, setPurchasePayments] = useState({ payments: [], total_paid: 0, balance_due: 0, total_cost: 0 });
  const [editingPayment, setEditingPayment] = useState(null);
  
  const [formData, setFormData] = useState({
    supplier_name: '',
    customer_id: '',
    payment_method: 'Cash',
    notes: '',
    purchase_date: new Date().toISOString().split('T')[0],
    items: [{ product_id: '', quantity: '', unit_cost: '', tax_bracket: 'B' }]
  });
  const [file, setFile] = useState(null);

  useEffect(() => {
    fetchPurchases();
    fetchProducts();
    fetchCustomers();
  }, []);

  const fetchPurchases = async () => {
    try {
      const response = await axios.get(`${API_URL}/purchases`);
      setPurchases(response.data);
    } catch (error) {
      toast.error('Failed to load purchases');
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API_URL}/products`);
      setProducts([...response.data].sort((a, b) => a.name.localeCompare(b.name)));
    } catch (error) {
      toast.error('Failed to load products');
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API_URL}/customers`);
      setCustomers(response.data);
    } catch (error) {
      toast.error('Failed to load customers');
    }
  };

  const handleAddItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, { product_id: '', quantity: '', unit_cost: '', tax_bracket: 'B' }]
    });
  };

  const handleRemoveItem = (index) => {
    if (formData.items.length > 1) {
      const newItems = formData.items.filter((_, i) => i !== index);
      setFormData({ ...formData, items: newItems });
    }
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index][field] = value;
    
    // Auto-fill unit cost and tax bracket from product if selecting a new product
    if (field === 'product_id') {
      const product = products.find(p => p.id === value);
      if (product) {
        if (!newItems[index].unit_cost) {
          newItems[index].unit_cost = product.base_price.toString();
        }
        newItems[index].tax_bracket = product.tax_bracket || 'B';
      }
    }
    
    setFormData({ ...formData, items: newItems });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate items
    const validItems = formData.items.filter(item => 
      item.product_id && item.quantity && item.unit_cost
    );
    
    if (validItems.length === 0) {
      toast.error('Please add at least one product with quantity and cost');
      return;
    }
    
    if (!file && !editingPurchase) {
      toast.error('Please attach a purchase order document');
      return;
    }

    const formDataToSend = new FormData();
    formDataToSend.append('supplier_name', formData.supplier_name);
    formDataToSend.append('items', JSON.stringify(validItems));
    
    if (formData.customer_id && formData.customer_id !== 'none') {
      formDataToSend.append('customer_id', formData.customer_id);
    }
    if (formData.notes) {
      formDataToSend.append('notes', formData.notes);
    }
    if (formData.purchase_date) {
      formDataToSend.append('purchase_date', formData.purchase_date);
    }
    if (file) {
      formDataToSend.append('file', file);
    }

    try {
      if (editingPurchase) {
        await axios.put(`${API_URL}/purchases/${editingPurchase.id}`, formDataToSend, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        toast.success('Purchase updated successfully');
      } else {
        await axios.post(`${API_URL}/purchases`, formDataToSend, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        toast.success('Purchase recorded successfully');
      }
      setDialogOpen(false);
      resetForm();
      fetchPurchases();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save purchase');
    }
  };

  const resetForm = () => {
    setFormData({
      supplier_name: '',
      customer_id: '',
      payment_method: 'Cash',
      notes: '',
      purchase_date: new Date().toISOString().split('T')[0],
      items: [{ product_id: '', quantity: '', unit_cost: '', tax_bracket: 'B' }]
    });
    setFile(null);
    setEditingPurchase(null);
  };

  const handleEdit = (purchase) => {
    setEditingPurchase(purchase);
    const items = purchase.items || [{
      product_id: purchase.product_id,
      quantity: purchase.quantity?.toString() || '',
      unit_cost: purchase.unit_cost?.toString() || ''
    }];
    
    setFormData({
      supplier_name: purchase.supplier_name || '',
      customer_id: purchase.customer_id || '',
      payment_method: purchase.payment_method || 'Cash',
      notes: purchase.notes || '',
      purchase_date: purchase.created_at?.split('T')[0] || new Date().toISOString().split('T')[0],
      items: items.map(item => ({
        product_id: item.product_id || '',
        quantity: item.quantity?.toString() || '',
        unit_cost: item.unit_cost?.toString() || '',
        tax_bracket: item.tax_bracket || 'B'
      }))
    });
    setDialogOpen(true);
  };

  const handleDeletePurchase = async (id) => {
    if (!window.confirm('Are you sure? This will also reverse the inventory changes.')) return;
    try {
      await axios.delete(`${API_URL}/purchases/${id}`);
      toast.success('Purchase deleted and inventory adjusted');
      fetchPurchases();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete purchase');
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedIds.length === 0) return;
    if (!window.confirm(`Delete ${selectedIds.length} purchases? This will also reverse inventory changes.`)) return;
    
    try {
      await axios.delete(`${API_URL}/purchases`, { data: selectedIds });
      toast.success(`Deleted ${selectedIds.length} purchases`);
      setSelectedIds([]);
      fetchPurchases();
    } catch (error) {
      toast.error('Failed to delete purchases');
    }
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedIds(filteredPurchases.map(p => p.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectOne = (id, checked) => {
    if (checked) {
      setSelectedIds([...selectedIds, id]);
    } else {
      setSelectedIds(selectedIds.filter(i => i !== id));
    }
  };

  const downloadPurchaseExcel = async (purchase) => {
    try {
      const response = await axios.get(`${API_URL}/purchases/${purchase.id}/excel`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `purchase_${purchase.id.substring(0, 8)}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
      toast.success('Excel downloaded');
    } catch (error) { toast.error('Failed to download Excel'); }
  };

  const filteredPurchases = purchases.filter(purchase => {
    const itemNames = purchase.items?.map(i => i.product_name).join(' ') || purchase.product_name || '';
    const matchesSearch = itemNames.toLowerCase().includes(searchTerm.toLowerCase()) ||
           (purchase.supplier_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
           (purchase.customer_name || '').toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesCustomer = customerFilter === 'all' || purchase.customer_id === customerFilter;
    
    // Period filter
    const purchaseDate = new Date(purchase.created_at);
    const matchesDateFrom = !periodFilter.startDate || purchaseDate >= new Date(periodFilter.startDate);
    const matchesDateTo = !periodFilter.endDate || purchaseDate <= new Date(periodFilter.endDate + 'T23:59:59');
    
    return matchesSearch && matchesCustomer && matchesDateFrom && matchesDateTo;
  });

  const [currentPage, setCurrentPage] = useState(1);
  const downloadFilteredExcel = async () => {
    if (filteredPurchases.length === 0) { toast.error('No purchases to export'); return; }
    try {
      const response = await axios.post(`${API_URL}/purchases/export`, filteredPurchases.map(p => p.id), { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `purchases_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
      toast.success(`Exported ${filteredPurchases.length} purchases`);
    } catch (error) { toast.error('Failed to export Excel'); }
  };

  const ITEMS_PER_PAGE = 25;
  const totalPages = Math.ceil(filteredPurchases.length / ITEMS_PER_PAGE) || 1;
  const safePage = Math.min(currentPage, totalPages);
  const paginatedPurchases = filteredPurchases.slice((safePage - 1) * ITEMS_PER_PAGE, safePage * ITEMS_PER_PAGE);

  const calculateFormTotal = () => {
    return formData.items.reduce((sum, item) => {
      const qty = parseFloat(item.quantity) || 0;
      const cost = parseFloat(item.unit_cost) || 0;
      return sum + (qty * cost);
    }, 0);
  };

  // Payment functions
  const openPaymentDialog = async (purchase) => {
    setPaymentPurchase(purchase);
    setPaymentForm({ amount: '', payment_method: purchase.payment_method || 'Cash', payment_date: new Date().toISOString().split('T')[0] });
    setEditingPayment(null);
    try {
      const response = await axios.get(`${API_URL}/purchases/${purchase.id}/payments`);
      setPurchasePayments(response.data);
    } catch (error) {
      setPurchasePayments({ payments: [], total_paid: 0, balance_due: purchase.total_cost, total_cost: purchase.total_cost });
    }
    setPaymentDialogOpen(true);
  };

  const fetchPaymentHistory = async () => {
    if (!paymentPurchase) return;
    try {
      const response = await axios.get(`${API_URL}/purchases/${paymentPurchase.id}/payments`);
      setPurchasePayments(response.data);
    } catch (error) {
      console.error('Failed to fetch payment history');
    }
  };

  const handleRecordPayment = async () => {
    if (!paymentPurchase || !paymentForm.amount || parseFloat(paymentForm.amount) <= 0) {
      toast.error('Please enter a valid payment amount');
      return;
    }
    try {
      const params = new URLSearchParams();
      params.append('payment_method', paymentForm.payment_method);
      params.append('amount', paymentForm.amount);
      if (paymentForm.payment_date) params.append('payment_date', paymentForm.payment_date);
      
      await axios.post(`${API_URL}/purchases/${paymentPurchase.id}/payment?${params.toString()}`);
      toast.success('Payment recorded successfully');
      setPaymentForm({ amount: '', payment_method: 'Cash', payment_date: new Date().toISOString().split('T')[0] });
      await fetchPaymentHistory();
      fetchPurchases();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record payment');
    }
  };

  const handleEditPayment = (payment) => {
    setEditingPayment(payment);
    setPaymentForm({ amount: payment.amount.toString(), payment_method: payment.payment_method, payment_date: (payment.date || payment.created_at || '').split('T')[0] || new Date().toISOString().split('T')[0] });
  };

  const handleUpdatePayment = async () => {
    if (!editingPayment || !paymentForm.amount || parseFloat(paymentForm.amount) <= 0) {
      toast.error('Please enter a valid payment amount');
      return;
    }
    try {
      const params = new URLSearchParams();
      params.append('amount', paymentForm.amount);
      params.append('payment_method', paymentForm.payment_method);
      if (paymentForm.payment_date) params.append('payment_date', paymentForm.payment_date);
      
      await axios.put(`${API_URL}/purchases/${paymentPurchase.id}/payments/${editingPayment.id}?${params.toString()}`);
      toast.success('Payment updated successfully');
      setEditingPayment(null);
      setPaymentForm({ amount: '', payment_method: 'Cash', payment_date: new Date().toISOString().split('T')[0] });
      await fetchPaymentHistory();
      fetchPurchases();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update payment');
    }
  };

  const handleDeletePayment = async (paymentId) => {
    if (!window.confirm('Are you sure you want to delete this payment?')) return;
    try {
      await axios.delete(`${API_URL}/purchases/${paymentPurchase.id}/payments/${paymentId}`);
      toast.success('Payment deleted successfully');
      await fetchPaymentHistory();
      fetchPurchases();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete payment');
    }
  };

  const cancelEditPayment = () => {
    setEditingPayment(null);
    setPaymentForm({ amount: '', payment_method: 'Cash', payment_date: new Date().toISOString().split('T')[0] });
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="text-muted-foreground">Loading...</div></div>;
  }

  return (
    <div className="space-y-6" data-testid="purchases-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-heading font-semibold text-foreground">Purchases</h1>
          <p className="text-muted-foreground">Record inventory purchases from suppliers</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={downloadFilteredExcel} data-testid="export-purchases-excel-btn">
            <FileSpreadsheet className="w-4 h-4 mr-2 text-green-700" />
            Download Excel
          </Button>
          {selectedIds.length > 0 && canDelete && (
            <Button variant="destructive" onClick={handleDeleteSelected} data-testid="delete-selected-btn">
              <Trash2 className="w-4 h-4 mr-2" />
              Delete ({selectedIds.length})
            </Button>
          )}
          <Button 
            onClick={() => { resetForm(); setDialogOpen(true); }}
            className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full" 
            data-testid="add-purchase-btn"
          >
            <Plus className="w-5 h-5 mr-2" />
            Record Purchase
          </Button>
        </div>
      </div>

      {/* Period Filter */}
      <PeriodFilter onFilterChange={setPeriodFilter} />

      {/* Search Bar */}
      <div className="bg-white p-4 rounded-xl border border-border shadow-sm">
        <div className="flex flex-col md:flex-row md:items-center gap-2">
          <div className="flex items-center space-x-2 flex-1">
            <Search className="w-5 h-5 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search by product, supplier, or customer..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="flex-1"
              data-testid="purchases-search-input"
            />
          </div>
          <Select value={customerFilter} onValueChange={setCustomerFilter}>
            <SelectTrigger className="w-full md:w-[220px]" data-testid="purchases-customer-filter">
              <SelectValue placeholder="Filter by customer" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Customers</SelectItem>
              {customers.map(c => (<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>))}
            </SelectContent>
          </Select>
          {periodFilter.label && (
            <span className="text-xs text-muted-foreground whitespace-nowrap">
              Showing {filteredPurchases.length} of {purchases.length} purchases
            </span>
          )}
        </div>
      </div>

      <div className="bg-white rounded-xl border border-border shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="purchases-table">
            <thead className="bg-muted/50 border-b border-border">
              <tr>
                <th className="px-4 py-4 text-left">
                  <Checkbox 
                    checked={selectedIds.length === filteredPurchases.length && filteredPurchases.length > 0}
                    onCheckedChange={handleSelectAll}
                    data-testid="select-all-checkbox"
                  />
                </th>
                <th className="px-4 py-4 text-left text-sm font-medium text-foreground">Date</th>
                <th className="px-4 py-4 text-left text-sm font-medium text-foreground">Products</th>
                <th className="px-4 py-4 text-left text-sm font-medium text-foreground">Customer</th>
                <th className="px-4 py-4 text-left text-sm font-medium text-foreground">Supplier</th>
                <th className="px-4 py-4 text-left text-sm font-medium text-foreground">Total</th>
                <th className="px-4 py-4 text-left text-sm font-medium text-foreground">Paid</th>
                <th className="px-4 py-4 text-left text-sm font-medium text-foreground">Balance</th>
                <th className="px-4 py-4 text-right text-sm font-medium text-foreground">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filteredPurchases.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-6 py-12 text-center text-muted-foreground">
                    {purchases.length === 0 ? 'No purchases recorded yet.' : 'No purchases match your search.'}
                  </td>
                </tr>
              ) : (
                paginatedPurchases.map((purchase) => (
                  <tr key={purchase.id} className="hover:bg-muted/30 transition-colors" data-testid="purchase-row">
                    <td className="px-4 py-4">
                      <Checkbox 
                        checked={selectedIds.includes(purchase.id)}
                        onCheckedChange={(checked) => handleSelectOne(purchase.id, checked)}
                      />
                    </td>
                    <td className="px-4 py-4 text-sm text-muted-foreground">
                      {new Date(purchase.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-4 text-sm">
                      {purchase.items ? (
                        <div className="space-y-1">
                          {purchase.items.slice(0, 2).map((item) => (
                            <div key={item.product_id || item.product_name} className="text-foreground">
                              {item.product_name} ({item.quantity} {item.unit || 'units'})
                            </div>
                          ))}
                          {purchase.items.length > 2 && (
                            <div className="text-muted-foreground text-xs">
                              +{purchase.items.length - 2} more items
                            </div>
                          )}
                        </div>
                      ) : (
                        <span>{purchase.product_name}</span>
                      )}
                    </td>
                    <td className="px-4 py-4 text-sm text-muted-foreground">
                      {purchase.customer_name || 'General Stock'}
                    </td>
                    <td className="px-4 py-4 text-sm text-muted-foreground">{purchase.supplier_name}</td>
                    <td className="px-4 py-4 text-sm font-semibold text-foreground">
                      RWF {formatNumber(purchase.total_cost)}
                    </td>
                    <td className="px-4 py-4 text-sm text-emerald-600">
                      {formatNumber(purchase.amount_paid || 0)}
                    </td>
                    <td className="px-4 py-4 text-sm">
                      {(purchase.balance_due || (purchase.total_cost - (purchase.amount_paid || 0))) > 0 ? (
                        <span className="text-amber-600 font-medium">RWF {formatNumber(purchase.balance_due || (purchase.total_cost - (purchase.amount_paid || 0)))}</span>
                      ) : (
                        <span className="text-emerald-600">Paid</span>
                      )}
                    </td>
                    <td className="px-4 py-4 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="sm" onClick={() => openPaymentDialog(purchase)} data-testid="record-payment-btn" title="Record Payment">
                          <DollarSign className="w-4 h-4 text-emerald-600" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={async () => {
                          try {
                            const response = await axios.get(`${API_URL}/purchases/${purchase.id}/document`, { responseType: 'blob' });
                            const blob = new Blob([response.data], { type: response.headers['content-type'] || 'application/octet-stream' });
                            const url = window.URL.createObjectURL(blob);
                            const link = document.createElement('a');
                            link.href = url;
                            link.download = purchase.purchase_order_filename || 'purchase_order.pdf';
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                            setTimeout(() => window.URL.revokeObjectURL(url), 1000);
                          } catch (error) { toast.error('Failed to download document'); }
                        }} data-testid="download-purchase-doc-btn" title="Download Document">
                          <Download className="w-4 h-4 text-blue-600" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => downloadPurchaseExcel(purchase)} data-testid="download-purchase-excel-btn" title="Download Excel">
                          <FileSpreadsheet className="w-4 h-4 text-green-700" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleEdit(purchase)} data-testid="edit-purchase-btn">
                          <Edit className="w-4 h-4" />
                        </Button>
                        {canDelete && (
                          <Button variant="ghost" size="sm" onClick={() => handleDeletePurchase(purchase.id)} data-testid="delete-purchase-btn">
                            <Trash2 className="w-4 h-4 text-destructive" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <Pagination page={currentPage} pages={totalPages} total={filteredPurchases.length} limit={ITEMS_PER_PAGE} onPageChange={setCurrentPage} />
      </div>

      {/* Purchase Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="purchase-dialog">
          <DialogHeader>
            <DialogTitle>{editingPurchase ? 'Edit Purchase' : 'Record New Purchase'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Supplier Name *</label>
                <Input
                  required
                  value={formData.supplier_name}
                  onChange={(e) => setFormData({ ...formData, supplier_name: e.target.value })}
                  placeholder="Enter supplier name"
                  data-testid="purchase-supplier-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Link to Customer (Optional)</label>
                <Select value={formData.customer_id} onValueChange={(value) => setFormData({ ...formData, customer_id: value })}>
                  <SelectTrigger data-testid="purchase-customer-select">
                    <SelectValue placeholder="Select customer for COGS" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No specific customer</SelectItem>
                    {customers.map(customer => (
                      <SelectItem key={customer.id} value={customer.id}>{customer.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Items Section */}
            <div className="border rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <label className="text-sm font-medium">Products *</label>
                <Button type="button" variant="outline" size="sm" onClick={handleAddItem}>
                  <Plus className="w-4 h-4 mr-1" /> Add Product
                </Button>
              </div>
              
              <div className="space-y-3">
                {formData.items.map((item, index) => (
                  <div key={index} className="grid grid-cols-12 gap-2 items-end">
                    <div className="col-span-4">
                      <Select value={item.product_id} onValueChange={(value) => handleItemChange(index, 'product_id', value)}>
                        <SelectTrigger data-testid={`item-product-select-${index}`}>
                          <SelectValue placeholder="Select product" />
                        </SelectTrigger>
                        <SelectContent>
                          {products.map(product => (
                            <SelectItem key={product.id} value={product.id}>
                              {product.name} ({product.unit})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-2">
                      <Input
                        type="number"
                        step="0.1"
                        placeholder="Qty"
                        value={item.quantity}
                        onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                        data-testid={`item-quantity-${index}`}
                      />
                    </div>
                    <div className="col-span-2">
                      <Input
                        type="number"
                        step="0.01"
                        placeholder="Unit Cost"
                        value={item.unit_cost}
                        onChange={(e) => handleItemChange(index, 'unit_cost', e.target.value)}
                        data-testid={`item-cost-${index}`}
                      />
                    </div>
                    <div className="col-span-3">
                      <Select value={item.tax_bracket || 'B'} onValueChange={(value) => handleItemChange(index, 'tax_bracket', value)}>
                        <SelectTrigger data-testid={`item-tax-bracket-${index}`}>
                          <SelectValue placeholder="Tax Cat." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="A">A (0%)</SelectItem>
                          <SelectItem value="B">B (18%)</SelectItem>
                          <SelectItem value="C">C (0%)</SelectItem>
                          <SelectItem value="D">D (0%)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-1">
                      {formData.items.length > 1 && (
                        <Button type="button" variant="ghost" size="sm" onClick={() => handleRemoveItem(index)}>
                          <X className="w-4 h-4 text-destructive" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="mt-3 pt-3 border-t text-right">
                <span className="text-sm text-muted-foreground">Total: </span>
                <span className="font-semibold">RWF {formatNumber(calculateFormTotal())}</span>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">
                Purchase Order Document {!editingPurchase && '*'}
              </label>
              <div className="flex items-center space-x-2">
                <Input
                  type="file"
                  required={!editingPurchase}
                  onChange={(e) => setFile(e.target.files[0])}
                  accept=".pdf,.jpg,.jpeg,.png"
                  data-testid="purchase-file-input"
                />
                <Upload className="w-5 h-5 text-muted-foreground" />
              </div>
              {file && <p className="text-xs text-muted-foreground mt-1">{file.name}</p>}
              {editingPurchase && !file && (
                <p className="text-xs text-muted-foreground mt-1">Leave empty to keep existing document</p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Purchase Date</label>
                <Input
                  type="date"
                  value={formData.purchase_date}
                  onChange={(e) => setFormData({ ...formData, purchase_date: e.target.value })}
                  data-testid="purchase-date-input"
                />
                <p className="text-xs text-muted-foreground mt-1">Change to backdate this purchase</p>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Notes (Optional)</label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Add any notes about this purchase..."
                  data-testid="purchase-notes-input"
                />
              </div>
            </div>

            <Button type="submit" className="w-full" data-testid="purchase-submit-btn">
              {editingPurchase ? 'Update Purchase' : 'Record Purchase'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Record Payment Dialog */}
      <Dialog open={paymentDialogOpen} onOpenChange={(open) => {
        setPaymentDialogOpen(open);
        if (!open) {
          setEditingPayment(null);
          setPaymentForm({ amount: '', payment_method: 'Cash', payment_date: new Date().toISOString().split('T')[0] });
        }
      }}>
        <DialogContent className="max-w-md" data-testid="purchase-payment-dialog">
          <DialogHeader>
            <DialogTitle>{editingPayment ? 'Edit Payment' : 'Record Purchase Payment'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Purchase Info */}
            <div className="bg-muted/30 p-4 rounded-lg space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Supplier:</span>
                <span className="font-medium">{paymentPurchase?.supplier_name}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Total Cost:</span>
                <span className="font-semibold">RWF {formatNumber(purchasePayments.total_cost || paymentPurchase?.total_cost)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Amount Paid:</span>
                <span className="text-emerald-600 font-medium">RWF {formatNumber(purchasePayments.total_paid)}</span>
              </div>
              <div className="flex justify-between text-sm border-t pt-2 mt-2">
                <span className="font-medium">Balance Due:</span>
                <span className="text-amber-600 font-bold">
                  RWF {formatNumber(purchasePayments.balance_due)}
                </span>
              </div>
            </div>

            {/* Payment History */}
            {purchasePayments.payments?.length > 0 && (
              <div>
                <label className="text-sm font-medium text-muted-foreground">Payment History</label>
                <div className="mt-2 space-y-2 max-h-40 overflow-y-auto">
                  {purchasePayments.payments.map((payment) => (
                    <div key={payment.id} className="flex justify-between items-center text-xs bg-muted/20 p-2 rounded">
                      <div>
                        <span>{new Date(payment.created_at).toLocaleDateString()}</span>
                        <span className="mx-2">-</span>
                        <span>{payment.payment_method}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">RWF {formatNumber(payment.amount)}</span>
                        <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => handleEditPayment(payment)}>
                          <Edit className="w-3 h-3" />
                        </Button>
                        <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => handleDeletePayment(payment.id)}>
                          <Trash2 className="w-3 h-3 text-destructive" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Payment Form */}
            {(purchasePayments.balance_due > 0 || editingPayment) && (
              <>
                <div>
                  <label className="block text-sm font-medium mb-2">
                    {editingPayment ? 'Edit Payment Amount (RWF)' : 'Payment Amount (RWF)'}
                  </label>
                  <Input
                    type="number"
                    step="0.01"
                    placeholder={editingPayment ? 'Enter amount' : `Max: ${formatNumber(purchasePayments.balance_due)}`}
                    value={paymentForm.amount}
                    onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
                    data-testid="purchase-payment-amount"
                  />
                  {!editingPayment && purchasePayments.balance_due > 0 && (
                    <div className="flex gap-2 mt-2">
                      <Button 
                        type="button" 
                        variant="outline" 
                        size="sm"
                        onClick={() => setPaymentForm({ 
                          ...paymentForm, 
                          amount: purchasePayments.balance_due.toString() 
                        })}
                      >
                        Pay Full Balance
                      </Button>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Payment Method</label>
                  <Select 
                    value={paymentForm.payment_method} 
                    onValueChange={(value) => setPaymentForm({ ...paymentForm, payment_method: value })}
                  >
                    <SelectTrigger data-testid="purchase-payment-method">
                      <SelectValue placeholder="Select method" />
                    </SelectTrigger>
                    <SelectContent>
                      {PAYMENT_METHODS.map(method => (
                        <SelectItem key={method} value={method}>{method}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Payment Date</label>
                  <Input
                    type="date"
                    value={paymentForm.payment_date}
                    onChange={(e) => setPaymentForm({ ...paymentForm, payment_date: e.target.value })}
                    data-testid="purchase-payment-date"
                  />
                </div>

                {editingPayment ? (
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={cancelEditPayment} className="flex-1">
                      Cancel
                    </Button>
                    <Button onClick={handleUpdatePayment} className="flex-1" data-testid="purchase-update-payment-btn">
                      <Edit className="w-4 h-4 mr-2" />
                      Update Payment
                    </Button>
                  </div>
                ) : (
                  <Button onClick={handleRecordPayment} className="w-full" data-testid="purchase-submit-payment-btn">
                    <DollarSign className="w-4 h-4 mr-2" />
                    Record Payment
                  </Button>
                )}
              </>
            )}

            {purchasePayments.balance_due <= 0 && !editingPayment && (
              <div className="text-center py-3 mb-3 bg-emerald-50 rounded-lg">
                <div className="text-emerald-600 font-semibold">Fully Paid</div>
                <p className="text-xs text-muted-foreground mt-1">Click on a payment above to edit it</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PurchasesPage;
